import { IAppointment, IUpdate, IUser, TMessageWithNextQuestionId, TMultyCompanyDetails } from "../../types/types";
import { defaultSettings, existingUserFlowId } from "../../utils/constants";
import { createSlice } from "@reduxjs/toolkit";
import { authApi } from "../api/auth.api";
import { RootState } from "../types";
import {
  IAllflowUser,
  IUserAuth,
  TQuestionairResponse,
} from "../types/auth.types";
import { deleteCookie, getCookie, setCookie } from "../../utils/token";
import { userApi } from "../api/user.api";
import { videoExamples } from "../../utils/videoExamples";
import { utilsApi } from "../api/utils.api";
import { paymentApi } from "../api/payment.api";
import {
  ITreatmentPlan,
  TCancelAppointmentReason,
  TGetQuestionByIdResponse,
  IProtocolComponent,
  IProtocolComponentsResponse,
} from "../types/user.types";
import { prevencherId } from "../../utils/prevencherForGuestsQuestions";

const accessToken = getCookie("accessToken");
const refreshToken = getCookie("refreshToken");
const activeContactToken = getCookie("activeContactToken");

const initialUser: IUser = {
  passport: "",
  roleId: 1,
  statusId: 0,
  id: "",
  guid: "",
  firstName: "",
  lastName: "",
  fullName: "",
  account: "",
  avatar: "",
  email: "",
  birth: new Date(),
  gender: 0,
  planId: 1,
  password: "",
  phone: "",
  address: "",
  plan: "",
  messages: [],
  updates: [],
  therapists: [],
  mockAppointments: [],
  settings: defaultSettings,
  prescriptions: [],
  invoices: [],
  unreadMessages: 0,
  videos: videoExamples,
  exercises: [],
  allAppointmentPrograms: [],
};

const initialState: IUserAuth = {
  user: initialUser,
  accessToken: accessToken,
  refreshToken: refreshToken,
  activeContactToken: activeContactToken,
  error: null,
  isAuth: false,
  currentUserFlowId: 0,
  nextStep: null,
  nextQuestionId: -1,
  currentQuestionnaireId: -1,
  currentQuestionnaire: null,
  currentQuestion: null,
  currentQuestionId: -1,
  isEnd: false,
  questionsOptions: [],
  endQuestion: "",
  startQuestion: "",
  prevencherChatMessages: [],
  prevencherPOCMessages: [],
  allAnswers: [],
  allAudioAnswers: [],
  allFileAnswers: [],
  allQuestions: [],
  currentQuestionnaireCompanies: null,
  companyId: 0,
  cancelAppointmentReasons: [],
  userProtocolComponents: [],
  questionnaireCapabilities: undefined,
};

const storeUser = (state: IUserAuth, { payload }: any) => {
  if (payload?.user) {
    for (let key in payload.user) {
      state.user[key.charAt(0).toLowerCase() + key.slice(1)] =
        payload.user[key];
    }
  }
  state.currentUserFlowId = existingUserFlowId;
};

const storeTokens = (state: IUserAuth, { payload }: any) => {
  const { accessToken, refreshToken, user, activeClient, activeClientToken, linkedClients } = payload;
  if (!accessToken) {
    return;
  }
  if (user && !activeClient) {
    storeUser(state, { payload });
    if (!linkedClients || linkedClients.length === 0) {
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
    }
  }
  state.error = payload.success ? null : payload.message;
  state.isAuth = payload.success ? payload.success : false;

  // linked clients (for contacts) handling
  if (linkedClients && Array.isArray(linkedClients)) {
    state.linkedClients = linkedClients.map((c: any) => ({
      clientId: c.clientId || "",
      guid: c.guid || undefined,
      fullName: c.fullName || "",
      birth: c.birth || undefined,
      relation: c.relation || undefined,
      roleId: c.roleId || 1, // assume patient roleId is 1 unless backend provided otherwise
    }));
    if (state.linkedClients.length >= 1) {
      if (activeClient && state.linkedClients.length === 1) {
        state.activeContact = payload.user
        state.activeContactToken = accessToken
        state.user = activeClient;
        state.accessToken = activeClientToken;
      } else { state.accessToken = "" }

      state.activeContact = payload.user
      state.activeContactToken = accessToken
    } else {
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
    }
  }
  if (state.accessToken) {
    setCookie("accessToken", state.accessToken, {
      //cookie expire on 12am.
      expires: (() => {
        const currentTime = new Date();
        const nextDay12AM = new Date(currentTime);
        nextDay12AM.setDate(currentTime.getDate() + 1);
        nextDay12AM.setHours(0, 0, 0, 0);
        //set cookie multiply by 1000 hence we divide by 1000.
        return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
      })(),
    });
  }
  if (state.refreshToken) {
    setCookie("refreshToken", state.refreshToken);
  }
  if (state.activeContactToken) {
    setCookie("activeContactToken", state.activeContactToken, {
      //cookie expire on 12am.
      expires: (() => {
        const currentTime = new Date();
        const nextDay12AM = new Date(currentTime);
        nextDay12AM.setDate(currentTime.getDate() + 1);
        nextDay12AM.setHours(0, 0, 0, 0);
        //set cookie multiply by 1000 hence we divide by 1000.
        return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
      })(),
    });
  }
};

/**
 * ✅ Normalizes questionnaire capabilities from server response
 * Handles both boolean and number (0/1) formats from DB
 * Returns normalized capabilities object or undefined if not available
 */
const normalizeQuestionnaireCapabilities = (questionnaire: any): {
  text: boolean;
  audioVideo: boolean;
  files: boolean;
  clinicalSummary: boolean;
} | undefined => {
  if (!questionnaire) return undefined;

  // Extract flags - handle both boolean and number (0/1) formats
  const analyzeText = questionnaire.AnalyzeText === true || questionnaire.AnalyzeText === 1 || questionnaire.AnalyzeText === "1";
  const analyzeAudioVideo = questionnaire.AnalyzeAudioVideo === true || questionnaire.AnalyzeAudioVideo === 1 || questionnaire.AnalyzeAudioVideo === "1";
  const analyzeFiles = questionnaire.AnalyzeFiles === true || questionnaire.AnalyzeFiles === 1 || questionnaire.AnalyzeFiles === "1";
  const generateClinicalSummary = questionnaire.GenerateClinicalSummary === true || questionnaire.GenerateClinicalSummary === 1 || questionnaire.GenerateClinicalSummary === "1";

  // Return capabilities object
  return {
    text: analyzeText,
    audioVideo: analyzeAudioVideo,
    files: analyzeFiles,
    clinicalSummary: generateClinicalSummary,
  };
};

const storeErrors = (state: IUserAuth, { payload }: any) => {
  const { data } = payload;
  state.error =
    typeof data?.message === "string"
      ? data.message
      : "משהו השתבש, אנא נסה שוב מאוחר יותר";
};

const storeAllflowUser = (
  state: IUserAuth,
  { payload }: { payload: IAllflowUser[] }
) => {
  return { ...state, allUserFlows: payload };
};

export const authUserSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    setPhone: (state, { payload }) => {
      return { ...state, user: { ...state.user, phone: payload } };
    },
    setLoginInfo: (
      state,
      {
        payload,
      }: { payload: { email: string; password: string; id: string | number } }
    ) => {
      return {
        ...state,
        user: {
          ...state.user,
          email: payload.email,
          password: payload.password,
          id: String(payload.id),
        },
      };
    },
    logout: () => {
      deleteCookie("accessToken");
      deleteCookie("activeContactToken");
      return { ...initialState, activeContactToken: null, activeContact: null };
    },
    setAppointment: (state, { payload }) => {
      const { therapist, date } = payload;
      const newAppointment = {
        id: state.user.mockAppointments.length,
        therapist,
        date,
      };
      return {
        ...state,
        user: {
          ...state.user,
          mockAppointments: [...state.user.mockAppointments, newAppointment],
        },
      };
    },
    updateStateAppointment: (state, { payload }) => {
      const appointment = state.user.mockAppointments.find(
        (a) => a.id === payload.appointmentId
      );
      if (appointment) appointment.date = payload.date;
    },

    setTherapist: (state, { payload }) => {
      const { therapist } = payload;
      const isMyTherapist = state.user.therapists.find(
        (obj) => obj.id === therapist.id
      );
      if (!isMyTherapist)
        return {
          ...state,
          user: {
            ...state.user,
            therapists: [...state.user.therapists, therapist],
          },
        };
    },
    setNextQuestion: (state: IUserAuth) => {
      state.nextQuestionId = 1;
    },
    setUserFlowId: (state: IUserAuth, { payload }) => {
      state.currentUserFlowId = payload;
    },
    setMessages: (state: IUserAuth, { payload }) => {
      return {
        ...state,
        prevencherChatMessages: [...state.prevencherChatMessages, payload],
        nextQuestionId: payload.nextQuestionId,
      };
    },
    setFileAnswers: (
      state: IUserAuth,
      { payload }: { payload: { questionId: number; file: File }[] }
    ) => {
      return {
        ...state,
        allFileAnswers: [...state.allFileAnswers, ...payload],
      };
    },

    resetFileAnswers: (state: IUserAuth) => {
      return {
        ...state,
        allFileAnswers: [],
      };
    },
    // ✅ Optimistic UI: עדכון הודעה קיימת לפי tempId או messageId
    updateMessage: (state: IUserAuth, { payload }: { payload: { tempId?: string; messageId?: string; updates: Partial<TMessageWithNextQuestionId> } }) => {
      const { tempId, messageId, updates } = payload;
      const messageIndex = state.prevencherChatMessages.findIndex(
        (msg) => (tempId && msg.tempId === tempId) || (messageId && msg.messageId === messageId)
      );
      if (messageIndex !== -1) {
        state.prevencherChatMessages[messageIndex] = {
          ...state.prevencherChatMessages[messageIndex],
          ...updates,
        };
      }
    },
    setQuestionOptions: (state: IUserAuth, { payload }) => {
      return {
        ...state,
        questionsOptions: payload,
      };
    },
    setCurrentQuestion: (state: IUserAuth, { payload }) => {
      if (
        state.prevencherChatMessages.at(-1)?.message != payload?.Questions?.Desc
      ) {
        return {
          ...state,
          currentQuestion: payload.Questions,
          prevencherChatMessages: [
            ...state.prevencherChatMessages,
            {
              messageId: payload.Questions.Id.toString(),
              id: prevencherId,
              date: new Date(),
              unread: false,
              message: payload.Questions.Desc,
              nextQuestionId: state.nextQuestionId,
            },
          ],
          questionsOptions: payload.QuestionsOptions || [],
        };
      }
    },
    setPOCMessages: (state: IUserAuth, { payload }) => {
      return {
        ...state,
        prevencherPOCMessages: [...state.prevencherPOCMessages, payload],
        nextQuestionId: payload.nextQuestionId,
      };
    },
    setOptions: (state: IUserAuth, { payload }) => {
      return {
        ...state,
        questionsOptions: payload,
      };
    },
    setAnswers: (state: IUserAuth, { payload }) => {
      const newAnswer = { ...payload, AnswerDate: payload.AnswerDate ?? null };
      return {
        ...state,
        allAnswers: [...state.allAnswers, newAnswer],
      };
    },
    setAudioAnswers: (
      state: IUserAuth,
      { payload }: { payload: { questionId: number; file: Blob; questionnaireId?: number } }
    ) => {
      // ✅ תיקון 2: שמירת מזהה לשיוך הקלטה לשאלון הנוכחי
      return {
        ...state,
        allAudioAnswers: [...state.allAudioAnswers, payload],
      };
    },
    resetAudioAnswers: (state: IUserAuth) => {
      return {
        ...state,
        allAudioAnswers: [],
      };
    },
    resetQuestionnair: (state: IUserAuth) => {
      return {
        ...state,
        currentQuestionnaireId: -1,
      };
    },
    clearRetryAfter: (state) => {
      return {
        ...state,
        retryAfterTimestamp: null,
      };
    },
    restoreContactSession: (state, { payload }) => {
      // Restore patient session when activeContactToken exists
      if (payload.success && payload.user) {
        state.isAuth = true;
        state.error = null;
        // Patient data becomes main user (from getUser with accessToken)
        storeUser(state, { payload });
      }
    },
    setContactDetails: (state, { payload }) => {
      if (payload.user) {
        // Store contact details (from getContactDetails with activeContactToken)
        state.activeContact = payload.user;

        state.linkedClients = payload.linkedClients || [];
      }
    },
    setSelectedCompany: (state, { payload }: { payload: TMultyCompanyDetails | null }) => {
      return {
        ...state,
        selectedCompany: payload,
        user: {
          ...state.user,
          companyId: payload?.CompanyId ?? 0,
        },
      };
    },
    switchPatient: (state, { payload }) => {
      // Update patient data
      if (payload.user) {
        storeUser(state, { payload });
      }
      // Update accessToken (patient token)
      if (payload.accessToken) {
        state.accessToken = payload.accessToken;
        setCookie("accessToken", payload.accessToken, {
          expires: (() => {
            const currentTime = new Date();
            const nextDay12AM = new Date(currentTime);
            nextDay12AM.setDate(currentTime.getDate() + 1);
            nextDay12AM.setHours(0, 0, 0, 0);
            return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
          })(),
        });
      }
      state.error = null;
    },

    clearContactSession: (state) => {
      // Clear contact session on token expiry
      deleteCookie("activeContactToken");
      state.activeContact = null;
      state.activeContactToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, storeTokens)
      .addMatcher(
        authApi.endpoints.login.matchRejected,
        (state: IUserAuth, { payload }: any) => {
          const { data, status } = payload;
          if (status === 429) {
            state.error = "יותר מדי ניסיונות התחברות. אנא נסו שוב מאוחר יותר.";
            state.retryAfterTimestamp = Date.now() + 60 * 1000;
          } else {
            state.error =
              typeof data?.message === "string"
                ? data?.message
                : "משהו השתבש, אנא נסה שוב מאוחר יותר";
          }
        }
      )
      .addMatcher(authApi.endpoints.loginGoogle.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.loginGoogle.matchRejected, storeErrors)
      .addMatcher(authApi.endpoints.loginWithCode.matchFulfilled, () => {})
      .addMatcher(authApi.endpoints.loginWithCode.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.authWithCode.matchFulfilled,
        storeTokens
      )
      .addMatcher(authApi.endpoints.authWithCode.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.authWithoutLogin.matchFulfilled,
        storeTokens
      )
      .addMatcher(authApi.endpoints.authWithoutLogin.matchRejected, storeErrors)
      .addMatcher(authApi.endpoints.register.matchFulfilled, storeTokens)
      // .addMatcher(authApi.endpoints.setUserPassword.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.register.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.sendCodeWithoutLogin.matchRejected,
        storeErrors
      )
      .addMatcher(
        authApi.endpoints.sendCodeWithoutLogin.matchFulfilled,
        (store) => {
          store.error = null;
        }
      )
      .addMatcher(userApi.endpoints.getUser.matchFulfilled, (state, action) => {
        storeUser(state, action);
        state.error = action.payload.success ? null : action.payload.message;
        state.isAuth = action.payload.success;
      })
      .addMatcher(
        utilsApi.endpoints.getAllflowUser.matchFulfilled,
        storeAllflowUser
      )
      .addMatcher(
        userApi.endpoints.getQuestionByQuestionnaireId.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TGetQuestionByIdResponse }
        ) => {
          state.isEnd = payload.Questions?.IsEnd;
          state.currentQuestion = payload.Questions;
          state.currentQuestionId = payload.Questions?.Id;
          state.questionsOptions = payload?.QuestionsOptions || [];
        }
      )
      .addMatcher(
        userApi.endpoints.getPOCQuestionById.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TGetQuestionByIdResponse }
        ) => {
          state.isEnd = payload.Questions?.IsEnd;
          state.currentQuestion = payload.Questions;
          state.currentQuestionId = payload.Questions?.Id;
          state.questionsOptions = payload?.QuestionsOptions || [];
          state.nextQuestionId = payload?.Questions.NextQuestionId;
          state.prevencherPOCMessages = [
            ...state.prevencherPOCMessages,
            {
              messageId: payload.Questions.Id.toString(),
              id: prevencherId,
              date: new Date(),
              unread: false,
              message: payload.Questions.Desc,
              nextQuestionId: state.nextQuestionId,
            },
          ];
        }
      )
      .addMatcher(
        userApi.endpoints.getNextQuestionByQuestionnaireId.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TGetQuestionByIdResponse }
        ) => {
          state.currentQuestion = payload.Questions;
          state.currentQuestionId = payload.Questions?.Id;
          state.questionsOptions = payload.QuestionsOptions || [];

          const index = state.prevencherChatMessages.findIndex(
            (m) => m.messageId.toString() === payload.Questions.Id.toString()
          );
          if (index === -1) {
            state.prevencherChatMessages = [
              ...state.prevencherChatMessages,
              {
                messageId: payload.Questions.Id.toString(),
                id: prevencherId,
                date: new Date(),
                message: payload.Questions.Desc,
                unread: false,
                nextQuestionId: state.nextQuestionId,
              },
            ];
          } else {
            state.prevencherChatMessages = [
              ...state.prevencherChatMessages.slice(0, index),
              {
                messageId: payload.Questions.Id.toString(),
                id: prevencherId,
                date: new Date(),
                message: payload.Questions.Desc,
                unread: false,
                nextQuestionId: state.nextQuestionId,
              },
            ];
          }

          // state.prevencherChatMessages = [
          //   ...state.prevencherChatMessages,
          //   {
          //     messageId: payload.Questions.Id.toString(),
          //     id: prevencherId,
          //     date: new Date(),
          //     unread: false,
          //     message: payload.Questions.Desc,
          //     nextQuestionId: state.nextQuestionId,
          //   },
          // ];
        }
      )
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state, action) => {
        deleteCookie("refreshToken");
        deleteCookie("accessToken");
        deleteCookie("activeContactToken");
        state.user = initialUser;
        state.accessToken = "";
        state.refreshToken = "";
        state.activeContactToken = "";
        state.activeContact = null;
        state.error = action.payload.success ? null : action.payload.message;
        state.isAuth = false;
      })
      .addMatcher(userApi.endpoints.getUsers.matchRejected, storeErrors)
      .addMatcher(
        userApi.endpoints.updateUserDetails.matchRejected,
        storeErrors
      )
      // .addMatcher(userApi.endpoints.getUserByGuid.matchRejected, storeErrors)
      // .addMatcher(
      //   userApi.endpoints.getUserByGuid.matchFulfilled,
      //   (state: IUserAuth, { payload }: { payload: IUser }) => {
      //     if (payload?.id) {
      //       for (let key in payload) {
      //         state.user[key.charAt(0).toLowerCase() + key.slice(1)] =
      //           payload[key];
      //       }
      //     }
      //   }
      // )
      .addMatcher(
        userApi.endpoints.getMessageByUserId.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: IUpdate[] }) => {
          state.user.updates = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.GetCurrentQuestionnaireByUsersId.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TQuestionairResponse }) => {
          if (!payload.error) {
            state.prevencherChatMessages = [];
            if (payload.Questionnaire?.StartQuestion)
              state.startQuestion = payload.Questionnaire?.StartQuestion;
            state.currentQuestionnaireId = payload.Questionnaire?.Id ?? -1;
            state.currentQuestionnaire = payload.Questionnaire || null;
            state.currentQuestionnaireCompanies =
              payload.QuestionnaireCompanies;
            state.nextQuestionId = payload.Questions
              ? payload.Questions[0]?.Id
              : -1;

            // ✅ Extract and store questionnaire capabilities from DB
            state.questionnaireCapabilities = normalizeQuestionnaireCapabilities(payload.Questionnaire);
          }
        }
      )
      .addMatcher(
        userApi.endpoints.GetCurrentQuestionnaireByUsersId.matchRejected,
        storeErrors
      )
      .addMatcher(
        userApi.endpoints.getAppointmentsByUserId.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: IAppointment[] }) => {
          state.user.appointments = payload;
        }
      )
      // .addMatcher(
      //   userApi.endpoints.getExercisesByUserId.matchFulfilled,
      //   (state: IUserAuth, { payload }: { payload: TExercise[] }) => {
      //     state.user.exercises = payload;
      //   }
      // )
      .addMatcher(
        userApi.endpoints.getQuestionnaireByGuidAndType.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TQuestionairResponse }) => {
          if (!payload.error) {
            state.currentQuestionnaireId = payload.Questionnaire?.Id ?? -1;
            state.currentQuestionnaire = payload.Questionnaire || null;
            state.currentQuestionnaireCompanies =
              payload?.QuestionnaireCompanies;
            state.nextQuestionId = payload?.Questions[0]?.Id;
            state.prevencherChatMessages = [];

            // ✅ Extract and store questionnaire capabilities from DB
            state.questionnaireCapabilities = normalizeQuestionnaireCapabilities(payload.Questionnaire);
          }
        }
      )
      .addMatcher(
        userApi.endpoints.getQuestionnaireByGuidAndType.matchPending,
        (state: IUserAuth) => {
          state.currentQuestionnaireId = -1;
          state.prevencherChatMessages = [];
          state.currentQuestion = null;
          state.currentQuestionId = -1;
          state.nextQuestionId = -1;
          state.questionsOptions = [];
          state.isEnd = false;
          // ✅ Clear capabilities when loading new questionnaire
          state.questionnaireCapabilities = undefined;
        }
      )
      .addMatcher(
        userApi.endpoints.getAllCancelAppointmentsReasons.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TCancelAppointmentReason[] }
        ) => {
          state.cancelAppointmentReasons = [...payload];
        }
      )
      .addMatcher(
        userApi.endpoints.insertAllAnswersQuestions.matchFulfilled,
        (state: IUserAuth) => {
          state.currentQuestion = null;
          state.currentQuestionId = -1;
        }
      )
      .addMatcher(
        userApi.endpoints.getAllOpenTreatmentPlansForUser.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: ITreatmentPlan[] }) => {
          state.user.allAppointmentPrograms = payload.filter(a => a.appointmentsProgram.isApproved);
        }
      )
      .addMatcher(
        userApi.endpoints.getProtocolComponents.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: IProtocolComponentsResponse }) => {
          if (payload.success) {
            state.userProtocolComponents = payload.components;
          }
        }
      )
      .addMatcher(
        paymentApi.endpoints.checkCard.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: string | false }) => {
          // Store last 4 digits of card in user state (including "0000" as valid)
          state.user.card = typeof payload === "string" ? payload : undefined;
        }
      )
      .addMatcher(
        paymentApi.endpoints.removeCard.matchFulfilled,
        (state: IUserAuth) => {
          state.user.card = undefined;
        }
      ).addMatcher(
        authApi.endpoints.SwitchCompanyUser.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: { accessToken: string; success: boolean } }) => {
          if (payload.accessToken) {
            state.accessToken = payload.accessToken;
            deleteCookie("accessToken");
            setCookie("accessToken", payload.accessToken, {
              expires: (() => {
                const currentTime = new Date();
                const nextDay12AM = new Date(currentTime);
                nextDay12AM.setDate(currentTime.getDate() + 1);
                nextDay12AM.setHours(0, 0, 0, 0);
                return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
              })(),
            });
          }
        }
      );
  },
});

export const { actions, reducer } = authUserSlice;
export default authUserSlice.reducer;
export const selectUser = (state: RootState) => state.user;
